import React, { useState } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  Stack,
  Icon,
  Flex,
} from '@chakra-ui/react';
import { CalendarIcon, TimeIcon } from '@chakra-ui/icons';
import WeeklyLineChart from './Weeklylinechart';
import Weekdaychart from './Weekdayschart';
import { MdMoreHoriz } from "react-icons/md";


function WeeklyTable({ rawLogs }) {
  const tableBg = useColorModeValue('gray.50', 'gray.700');
  const [selectedWeekRange, setSelectedWeekRange] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dailyBreakdown, setDailyBreakdown] = useState([]);
  

  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    d.setDate(d.getDate() - day);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const formatDate = (date) =>
    new Date(date).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });

  const formatFullDate = (date) =>
    new Date(date).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });

  const formatDuration = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = Math.floor(minutes % 60);
    const s = Math.round((minutes - Math.floor(minutes)) * 60);
    return `${h}h ${m}m ${s}s`;
  };

  const formatKey = (date) => {
    const d = new Date(date);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const processLogs = () => {
    const weekMap = {};

    rawLogs.forEach(({ on, off }) => {
      const onDate = new Date(on);
      const offDate = new Date(off);

      let curr = new Date(onDate);

      while (curr < offDate) {
        const endOfDay = new Date(curr);
        endOfDay.setHours(23, 59, 59, 999);

        const sessionEnd = new Date(Math.min(offDate, endOfDay));
        const duration = (sessionEnd - curr) / 60000;

        const dayKey = formatDate(curr);
        const weekStartDate = getWeekStart(curr);
        const weekKey = formatKey(weekStartDate);

        if (!weekMap[weekKey]) {
          weekMap[weekKey] = {
            weekStartDate,
            totalMinutes: 0,
            days: {},
          };
        }

        weekMap[weekKey].totalMinutes += duration;
        weekMap[weekKey].days[dayKey] = (weekMap[weekKey].days[dayKey] || 0) + duration;

        // Move to start of next day
        curr = new Date(sessionEnd);
        curr.setHours(24, 0, 0, 0);
      }
    });

    return Object.values(weekMap).sort((a, b) =>
      b.weekStartDate - a.weekStartDate
    );
  };

  const openModal = (week) => {
    const start = week.weekStartDate;
    const end = new Date(start);
    end.setDate(start.getDate() + 6);

    const rangeString = `${formatFullDate(start)} – ${formatFullDate(end)}`;
    setSelectedWeekRange(rangeString);

    const breakdown = Object.entries(week.days).map(([date, mins]) => ({
      date,
      duration: formatDuration(mins),
    }));

    setDailyBreakdown(breakdown);
    setIsModalOpen(true);
  };

  const weeks = processLogs();

  // Get the latest week
// Make a copy and sort by weekStartDate (newest last)
const sortedWeeks = [...weeks].sort(
  (a, b) => new Date(a.weekStartDate) - new Date(b.weekStartDate)
);

// Now grab the latest
const latestWeek = sortedWeeks.length > 0 ? sortedWeeks[sortedWeeks.length - 1] : null;


  return (
    <Box >
      {latestWeek && (
  <Text fontSize="sm" fontWeight="bold" align="center" mt={-5} mb={4}>
    Current Week Total: {formatDuration(latestWeek.totalMinutes)}
  </Text>
)}


      <Box m="auto" mt={10} w="70%" mb={10}>
        <WeeklyLineChart weeklyData={weeks} />
      </Box>

      <Box className='daily' w="60%" mx="auto" mt={10}>
        <Heading size="md" mb={4}>📅 Weekly Intelli Assign ON Hours</Heading>
        <Table variant="striped" bg={tableBg} borderRadius="md">
          <Thead>
            <Tr>
              <Th>Week</Th>
              <Th>Total Hours</Th>
            </Tr>
          </Thead>

          <Tbody>
            {weeks.map((week, index) => (
              <Tr
                key={index}
                onClick={() => openModal(week)}
                cursor="pointer"
                _hover={{
                  transform: "scale(1.03)", // makes it 5% bigger
                  transition: "transform 0.3s ease-in-out",
                  fontWeight: "600"
                }}

              >
                <Td>{formatFullDate(week.weekStartDate)}</Td>
                <Td>{formatDuration(week.totalMinutes)} </Td>
                <Td> <MdMoreHoriz /> </Td>

              </Tr>
            ))}
          </Tbody>
        </Table>

        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size="xl" isCentered scrollBehavior="inside">
          <ModalOverlay bg='blackAlpha.300' backdropFilter='blur(5px) hue-rotate(90deg)' />
          <ModalContent>
            <ModalHeader>🗓 {selectedWeekRange}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Weekdaychart dailyData={dailyBreakdown} />
              <Flex justify="center" mb={3}>
                <Text fontSize="sm" color="gray.500">
                  Total Hours:{' '}
                  <Text as="span" fontWeight="bold" fontSize="md" color="black">
                    {weeks.find(w => formatKey(w.weekStartDate) === formatKey(new Date(selectedWeekRange.split(' – ')[0])))?.totalMinutes
                      ? formatDuration(
                        weeks.find(
                          w => formatKey(w.weekStartDate) === formatKey(new Date(selectedWeekRange.split(' – ')[0]))
                        ).totalMinutes
                      )
                      : '0h 0m 0s'}
                  </Text>
                </Text>

              </Flex>

              <Stack spacing={3}>
                {dailyBreakdown.map((day, i) => (
                  <Box
                    key={i}
                    p={3}
                    borderWidth="1px"
                    borderRadius="lg"
                    shadow="sm"
                    _hover={{ shadow: 'md' }}
                  >
                    <Stack direction="row" align="center">
                      <Icon as={CalendarIcon} boxSize={5} color="green.500" />
                      <Text fontWeight="bold">{day.date}</Text>
                    </Stack>
                    <Stack direction="row" align="center" mt={2} ml={6}>
                      <Icon as={TimeIcon} boxSize={4} color="blue.500" />
                      <Text>{day.duration}</Text>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </ModalBody>
            <ModalFooter>
              <Text fontSize="sm" color="gray.500">
  Total Hours:{' '}
  <Text as="span" fontWeight="bold" fontSize="lg" color="orange.500">
    {weeks.find(w => formatKey(w.weekStartDate) === formatKey(new Date(selectedWeekRange.split(' – ')[0])))?.totalMinutes
      ? formatDuration(
          weeks.find(
            w => formatKey(w.weekStartDate) === formatKey(new Date(selectedWeekRange.split(' – ')[0]))
          ).totalMinutes
        )
      : '0h 0m 0s'}
  </Text>
</Text>

            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    </Box>
  );
}

export default WeeklyTable;
